import React, { useEffect, useState, useCallback } from "react";
import { Save, RefreshCcw } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function InventorySettings() {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const [costingMethod, setCostingMethod] = useState("WAC"); // WAC or FIFO

  const load = useCallback(async () => {
    if (!companyId) return;
    setErr("");
    setSaved(false);

    const { data, error } = await supabase
      .from("company_inventory_settings")
      .select("company_id,costing_method")
      .eq("company_id", companyId)
      .maybeSingle();

    if (error) {
      setErr(error.message);
      return;
    }

    if (data?.costing_method) setCostingMethod(data.costing_method);
    else setCostingMethod("WAC");
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    if (!companyId) return;
    setBusy(true);
    setErr("");
    setSaved(false);

    try {
      // Important warning: changing costing method mid-stream should be controlled.
      // We allow it, but you should do it only when no stock exists or at period boundaries.
      const { error } = await supabase
        .from("company_inventory_settings")
        .upsert(
          {
            company_id: companyId,
            costing_method: costingMethod,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "company_id" }
        );

      if (error) throw error;

      setSaved(true);
    } catch (e) {
      setErr(e.message || "Failed to save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Inventory Settings</h2>
          <p>
            Choose costing method. This affects how stock OUT cost is computed.
          </p>
        </div>
        <div className="fa-actions">
          <button className="fa-btn" onClick={load} disabled={busy}>
            <RefreshCcw size={16} /> Refresh
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={save}
            disabled={busy}
          >
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="fa-panel">
        <div className="fa-card">
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Costing Method</div>

          <div className="fa-toolbar">
            <select
              className="fa-select"
              value={costingMethod}
              onChange={(e) => setCostingMethod(e.target.value)}
              disabled={busy}
            >
              <option value="WAC">Weighted Average Cost (WAC)</option>
              <option value="FIFO">FIFO (First-In, First-Out)</option>
            </select>
          </div>

          <div
            style={{
              marginTop: 10,
              opacity: 0.85,
              fontSize: 13,
              lineHeight: "1.35rem",
            }}
          >
            <b>WAC</b> is simpler and stable for SMEs. <b>FIFO</b> is more
            detailed and uses cost layers.
            <div style={{ marginTop: 8, opacity: 0.85 }}>
              If you change this after posting stock, you may create valuation
              mismatches. Best practice: switch at a new period with clean stock
              or after an inventory count adjustment.
            </div>
          </div>

          {saved && (
            <div
              style={{
                marginTop: 10,
                color: "rgba(120,255,190,0.9)",
                fontWeight: 900,
              }}
            >
              Saved.
            </div>
          )}
          {err && (
            <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
              {err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
